import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Route, Router, Switch } from 'react-router-dom';
import { useFonts } from 'expo-font';

import { Environment } from './Environment';
import { history } from './util/History';
import { PrivateRoute, Role } from './components/PrivateRoute';
import { homeContainer } from './components/containers/HomeContainer';
import { studentListContainer } from './components/containers/StudentListContainer';
import { studentResultsUploadContainer } from './components/containers/StudentResultsUploadContainer';
import { HomeScreen } from './components/screens/HomeScreen';
import { StudentListScreen } from './components/screens/StudentListScreen';
import { StudentResultsUploadScreen } from './components/screens/StudentResultsUploadScreen';
import { Auth0Provider } from './components/Auth0Provider';
import { studentRecordContainer } from './components/containers/StudentRecordContainer';
import { StudentRecordScreen } from './components/screens/StudentRecordScreen';
import { transcriptRequestContainer } from './components/containers/RequestTranscriptContainer';
import { TranscriptRequestScreen } from './components/screens/RequestTranscriptScreen';
import { academicOverviewContainer } from './components/containers/AcademicOverviewContainer';
import { AcademicOverviewScreen } from './components/screens/AcademicOverviewScreen';
import { courseDetailsContainer } from './components/containers/CourseDetailsContainer';
import { CourseDetailsScreen } from './components/screens/CourseDetailsScreen';
import { profileContainerV2 } from './components/containers/ProfileContainerV2';
import { ProfileScreenV2 } from './components/screens/ProfileScreenV2';
import { MNOHSFloatingIndicator } from './components/MNOHSFloatingIndicator';
import { MNOHS404 } from './components/MNOHS404';
import { studentsUploadContainer } from './components/containers/StudentsUploadContainer';
import { StudentsUploadScreen } from './components/screens/StudentsUploadScreen';
import { notificationsContainer } from './components/containers/NotificationsContainer';
import { NotificationsScreen } from './components/screens/NotificationsScreen';
import { studentCourseDetailsContainer } from './components/containers/StudentCourseDetailsContainer';
import { StudentCourseDetailsScreen } from './components/screens/StudentCourseDetailsScreen';
import { transcriptHistoryContainer } from './components/containers/TranscriptHistoryContainer';
import { TranscriptHistoryScreen } from './components/screens/TranscriptHistoryScreen';

// List of screens
const Containers = {
  Home: homeContainer(HomeScreen),
  StudentList: studentListContainer(StudentListScreen),
  StudentsUpload: studentsUploadContainer(StudentsUploadScreen),
  StudentResultsUpload: studentResultsUploadContainer(StudentResultsUploadScreen),
  StudentRecord: studentRecordContainer(StudentRecordScreen),
  RequestTranscript: transcriptRequestContainer(TranscriptRequestScreen),
  AcademicOverview: academicOverviewContainer(AcademicOverviewScreen),
  CourseDetails: courseDetailsContainer(CourseDetailsScreen),
  UserProfile: profileContainerV2(ProfileScreenV2),
  Notifications: notificationsContainer(NotificationsScreen),
  StudentCourseDetails: studentCourseDetailsContainer(StudentCourseDetailsScreen),
  TranscriptHistory: transcriptHistoryContainer(TranscriptHistoryScreen),
};

export const Routes = () => {
  const { config } = Environment.current();

  const [loaded, error] = useFonts({
    MontserratRegular: require('../assets/fonts/Montserrat-Regular.ttf'),
    MontserratBold: require('../assets/fonts/Montserrat-Bold.ttf'),
    MontserratSemiBold: require('../assets/fonts/Montserrat-SemiBold.ttf'),
  });

  if (!loaded) {
    return <MNOHSFloatingIndicator block={true} padded={true} />;
  }
  if (error) {
    throw new Error();
  }

  return (
    <Router history={history}>
      <Auth0Provider
        domain={config.authDomain}
        client_id={config.authClientId}
        redirect_uri={window.location.origin}
        onRedirect={() => history.push(window.location.pathname)}>
        <Switch>
          <Route exact={true} path="/" component={Containers.Home} />
          <Route path="/404" component={MNOHS404} />
          <PrivateRoute allowedRoles={[Role.Admin]} path="/overall-student-list" component={Containers.StudentList} />
          <PrivateRoute
            allowedRoles={[Role.Admin]}
            path="/transcript-requests"
            component={Containers.TranscriptHistory}
          />
          <PrivateRoute allowedRoles={[Role.Admin]} path="/upload-students" component={Containers.StudentsUpload} />
          <PrivateRoute
            allowedRoles={[Role.Admin]}
            path="/upload-student-results"
            component={Containers.StudentResultsUpload}
          />
          <PrivateRoute
            allowedRoles={[Role.Admin]}
            exact={true}
            path="/student-record/:id"
            component={Containers.StudentRecord}
          />
          <PrivateRoute
            allowedRoles={[Role.Admin]}
            path="/student-record/:id/:course"
            component={Containers.StudentCourseDetails}
          />
          <PrivateRoute
            allowedRoles={[Role.Student]}
            path="/request-transcript"
            component={Containers.RequestTranscript}
          />
          <PrivateRoute
            allowedRoles={[Role.Student]}
            exact={true}
            path="/academic-overview"
            component={Containers.AcademicOverview}
          />
          <PrivateRoute
            allowedRoles={[Role.Student]}
            path="/academic-overview/:course"
            component={Containers.CourseDetails}
          />
          <PrivateRoute allowedRoles={[Role.Student]} path="/profile" component={Containers.UserProfile} />
          <PrivateRoute
            allowedRoles={[Role.Student, Role.Admin]}
            path="/notifications"
            component={Containers.Notifications}
          />
        </Switch>
      </Auth0Provider>
    </Router>
  );
};
